/**
 * ProyectoThumb — miniatura de proyecto/línea/componente con fallback de iniciales.
 * Click sobre la imagen → zoom en Dialog. Si no hay imagen → círculo con iniciales del cliente.
 */
import React, { useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cloudinaryThumb, uploadToCloudinary } from "@/lib/cloudinary";
import { toast } from "sonner";
import { Loader2, Camera, X } from "lucide-react";

function iniciales(nombre) {
  const parts = (nombre || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function ProyectoThumb({ url, nombre = "", size = 36, rounded = "full", className = "", "data-testid": testid }) {
  const [zoom, setZoom] = useState(false);
  const radius = rounded === "full" ? "rounded-full" : "rounded-lg";

  if (!url) {
    return (
      <div
        className={`${radius} bg-zinc-800 border border-zinc-700 text-zinc-300 font-bold flex items-center justify-center shrink-0 select-none ${className}`}
        style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.34)) }}
        title={nombre} data-testid={testid}>
        {iniciales(nombre)}
      </div>
    );
  }

  return (
    <>
      <img
        src={cloudinaryThumb(url, size * 3, size * 3)} alt={nombre || "imagen"}
        className={`${radius} object-cover border border-zinc-700 shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity ${className}`}
        style={{ width: size, height: size }}
        title={nombre} data-testid={testid}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setZoom(true); }}
      />
      {zoom && (
        <Dialog open onOpenChange={() => setZoom(false)}>
          <DialogContent
            className="bg-zinc-950 border-zinc-800 max-w-3xl p-2"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            data-testid="proyecto-thumb-zoom">
            <img src={url} alt={nombre || "imagen"} className="w-full max-h-[80vh] object-contain rounded" />
            {nombre && <div className="text-xs text-zinc-400 text-center pb-1">{nombre}</div>}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/** Control de subida/cambio/eliminación de imagen para formularios (solo oficina). */
export function ImagenUploadControl({ value, nombre = "", onChange, folder = "dnf/proyectos", size = 52, label = "Imagen del proyecto" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { secure_url } = await uploadToCloudinary(file, folder);
      onChange(secure_url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error("Error subiendo imagen: " + (err?.message || ""));
    } finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-3">
      <ProyectoThumb url={value} nombre={nombre} size={size} rounded="lg" data-testid="imagen-upload-preview" />
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid="imagen-upload-btn"
            className="text-[11px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 inline-flex items-center gap-1 transition-colors">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            {uploading ? "Subiendo…" : value ? "Cambiar" : "Añadir imagen"}
          </button>
          {value && !uploading && (
            <button type="button" onClick={() => onChange(null)}
              data-testid="imagen-remove-btn"
              className="text-[11px] px-2 py-1 rounded text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 inline-flex items-center gap-1 transition-colors">
              <X className="w-3 h-3" /> Quitar
            </button>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}

export default ProyectoThumb;
